import { Button } from '@kerfjs/ui/button';
import { px } from '@kerfjs/ui/css-values';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { Grid } from '@kerfjs/ui/grid';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { LoadingSpinner } from '@kerfjs/ui/loading-spinner';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';
import { Paperclip } from 'lucide';

import {
  type IntakeDoc,
  matrixFocus,
  matrixSelection,
  quoteMatrix,
  quoteMatrixLoadState,
} from '../../../data/state.js';
import { quotationNeedsReview } from '../../../lib/attachment-processing.js';
import { PriceMatrix } from './price-matrix.js';
import { QuoteDialog } from './quote-dialog.js';
import { VendorCard } from './vendor-card.js';

export function QuotesSegment({
  readOnly,
  filesByKind,
}: {
  readOnly: boolean;
  filesByKind: Record<string, IntakeDoc[]>;
}): SafeHtml {
  const m = quoteMatrix.value;
  const emptyUploads = Object.values(filesByKind).flat().filter(quotationNeedsReview);
  if (m === null) {
    const loadState = quoteMatrixLoadState.value;
    return (
      <List className="quotes-segment" gap="m">
        {loadState.status === 'error' ? (
          <StateBanner
            tone="danger"
            title="Quotes Unavailable"
            detail={loadState.message}
            action={
              <Button tone="neutral" emphasis="primary" size="compact" action="retry-quotes">
                Retry
              </Button>
            }
          />
        ) : (
          <LoadingSpinner label="Loading quotes" />
        )}
      </List>
    );
  }
  const rateNote =
    Object.keys(m.rates).length > 0
      ? `Rates: ${Object.entries(m.rates)
          .map(([c, r]) => `${c} = ${String(r)} ${m.base}`)
          .join(', ')}`
      : `No exchange rates set — foreign-currency quotes show as entered. Add rates in Settings.`;

  const hasContent = m.columns.length > 0 || m.unmatched.length > 0;
  const unpricedCount = m.lineItems.filter(
    (line) => !m.columns.some((column) => (m.cells[line.id]?.[column.quotationId]?.basePrice ?? null) !== null),
  ).length;
  return (
    <List className="quotes-segment quotes-box" gap="l" controlInsets="tb" rootAttributes={{ 'data-quote-drop': '' }}>
      {emptyUploads.length > 0 ? (
        <StateBanner
          tone="warning"
          title={`${String(emptyUploads.length)} quotation ${emptyUploads.length === 1 ? 'file produced' : 'files produced'} no quotations.`}
          detail={
            readOnly
              ? 'Review the flagged file in Documents. A reviewer can correct its type or add a quotation.'
              : 'Review the flagged file in Documents. You can correct its type or add a quotation manually.'
          }
        />
      ) : null}
      {!hasContent ? (
        readOnly ? (
          <EmptyState title="No Quotations" />
        ) : (
          <EmptyState
            title="Drop a vendor quote or comparison sheet here."
            detail="You can also add a quotation by hand. The assistant reads either and figures out which it is."
            icon={<LucideIcon icon={Paperclip} name="" />}
          />
        )
      ) : null}

      {m.columns.length > 0 ? (
        <List className="quotes-section" gap="xs">
          <ListHeader
            label="Vendors"
            count={m.columns.length}
            countLabel={`${String(m.columns.length)} quotation${m.columns.length === 1 ? '' : 's'}`}
          />
          {/* Kerf Grid auto-fill keeps a sparse row at the shared track width
                (HS2-GGJR1Z). Each ContentItem card owns its 8px inline margin, so
                the track minimum includes it (at least 224px of card). */}
          <Grid
            minColumnWidth={px(240)}
            autoFill
            gap={{ column: 'none', row: 's' }}
            rootAttributes={{ 'data-vendor-cards': '' }}
          >
            {m.columns.map((col) => {
              const lineCount = m.lineItems.filter(
                (li) => (m.cells[li.id]?.[col.quotationId]?.basePrice ?? null) !== null,
              ).length;
              return <VendorCard column={col} baseCurrency={m.base} lineCount={lineCount} readOnly={readOnly} />;
            })}
          </Grid>
        </List>
      ) : null}

      {!readOnly && m.duplicateVendorGroups.length > 0 ? (
        <List gap="xs">
          {m.duplicateVendorGroups.map((g) => (
            <div class="dup-banner" data-key={g.key}>
              <StateBanner
                tone="info"
                title={`${g.vendorNames.slice(1).join(', ')} looks like ${g.vendorNames[0] ?? ''}. Merge?`}
                action={
                  <Button
                    tone="neutral"
                    emphasis="primary"
                    size="compact"
                    rootAttributes={{
                      'data-vendor-merge': `${g.vendorIds.slice(1).join(',')}:${g.vendorIds[0] ?? ''}`,
                    }}
                  >
                    {`Merge into ${g.vendorNames[0] ?? ''}`}
                  </Button>
                }
              />
            </div>
          ))}
        </List>
      ) : null}

      {m.mixedBasis ? (
        <StateBanner
          tone="warning"
          className="warn-banner"
          title="Quotes use different price bases."
          detail="Ex-works vs delivered are not directly comparable until normalized; set each vendor's basis below."
        />
      ) : null}

      {m.columns.length === 0 ? null : (
        <List className="quotes-section" gap="xs">
          <ListHeader
            label="Price Matrix"
            badge={
              unpricedCount > 0 ? (
                <span>{`${String(unpricedCount)} line${unpricedCount === 1 ? '' : 's'} unpriced`}</span>
              ) : undefined
            }
            status={
              m.unmatched.length > 0 ? (
                <span>{`${String(m.unmatched.length)} unmatched quote line${m.unmatched.length === 1 ? '' : 's'}`}</span>
              ) : undefined
            }
            indicatorTone="danger"
          />
          <PriceMatrix
            matrix={m}
            readOnly={readOnly}
            selectedCells={matrixSelection.value.selected}
            focus={matrixFocus.value}
            edgeToEdge
          />
        </List>
      )}

      <ListInsetControl className="rate-note">
        <Text tone="quiet" size="compact">
          {rateNote}
        </Text>
      </ListInsetControl>
      {/* The notice banner renders in the editor header area (HS2-BFHGES). */}
      <QuoteDialog matrix={m} />
    </List>
  );
}
